import { z } from 'zod';
import React from 'react';
import { CmsShortTextSchema, CmsLinkSchema } from '$cms/types';
import type { SchemaType } from '$util/schema';
import type { ContentComponent } from '$cms/ContentComponent';
import { makeContentComponentDefaultConfig } from '$util/makeContentComponentDefaultConfig';
import { BrandedIconListCard } from '@/components/molecules/BrandedIconListCard';

const defaultConfiguration = makeContentComponentDefaultConfig({
  colour: 'purple' as ConfigColor,
});

const ConfigColorSchema = z.union([z.literal('purple'), z.literal('green')]);
type ConfigColor = z.infer<typeof ConfigColorSchema>;

const CmsBrandedIconSchema = {
  translatedEntities: z.object({
    icon1Id: CmsShortTextSchema,
    icon2Id: CmsShortTextSchema,
    icon3Id: CmsShortTextSchema,
    icon4Id: CmsShortTextSchema,
    icon5Id: CmsShortTextSchema.optional(),
    text1Content: CmsShortTextSchema,
    text2Content: CmsShortTextSchema,
    text3Content: CmsShortTextSchema,
    text4Content: CmsShortTextSchema,
    text5Content: CmsShortTextSchema.optional(),
    sentence1Content: CmsShortTextSchema,
    sentence2Content: CmsShortTextSchema,
    sentence3Content: CmsShortTextSchema,
    sentence4Content: CmsShortTextSchema,
    sentence5Content: CmsShortTextSchema.optional(),
    speechBubbleText: CmsShortTextSchema.optional(),
    modalSpeechBubbleText: CmsShortTextSchema.optional(),
    link: CmsLinkSchema.optional(),
  }),
  configuration: z
    .object({
      lazyLoadImages: z.boolean().optional(),
      preloadImages: z.boolean().optional(),
      colour: ConfigColorSchema.optional(),
    })
    .default(defaultConfiguration),
};

export type CmsBrandedIconListCardTranslatedEntities = SchemaType<
  (typeof CmsBrandedIconSchema)['translatedEntities']
>;
export type CmsBrandedIconListCardConfig = SchemaType<(typeof CmsBrandedIconSchema)['configuration']>;

export const CmsBrandedIconListCard: ContentComponent<
  CmsBrandedIconListCardTranslatedEntities,
  CmsBrandedIconListCardConfig
> = function CmsBrandedIconListCard({ translatedEntities, configuration, className }): JSX.Element {
  const {
    icon1Id,
    icon2Id,
    icon3Id,
    icon4Id,
    icon5Id,
    text1Content,
    text2Content,
    text3Content,
    text4Content,
    text5Content,
    sentence1Content,
    sentence2Content,
    sentence3Content,
    sentence4Content,
    sentence5Content,
    ...otherEntities
  } = translatedEntities;

  return (
    <BrandedIconListCard
      {...{
        itemIcons: {
          1: icon1Id,
          2: icon2Id,
          3: icon3Id,
          4: icon4Id,
          5: icon5Id,
        },
        itemText: {
          1: text1Content,
          2: text2Content,
          3: text3Content,
          4: text4Content,
          5: text5Content,
        },
        itemSentence: {
          1: sentence1Content,
          2: sentence2Content,
          3: sentence3Content,
          4: sentence4Content,
          5: sentence5Content,
        },
        ...otherEntities,
        ...configuration,
        className,
      }}
    />
  );
};
CmsBrandedIconListCard.displayName = 'CmsBrandedIconListCard';
CmsBrandedIconListCard.schema = CmsBrandedIconSchema;
